import { useState } from "react";

import CheckoutStepper from "@app/analytics/account/Billing/components/CheckoutStepper";
import useCheckout from "@app/analytics/account/Billing/hooks/useCheckout";
import type {
  BillingCreditCheckoutStep,
  CreditPackage,
  PaymentFormElementOptions,
} from "@app/analytics/account/Billing/types";
import * as BillingUtils from "@app/analytics/account/Billing/utils";
import { showToast } from "@app/common/hooks/useToasts";
import type {
  CheckoutBillingInfo,
  InitiateCheckoutResponse,
  PreviewInvoice,
} from "@app/common/types";
import usePageTitle from "@app/utils/hooks/usePageTitle";
import { titleize } from "@app/utils/utils";

import LoadSpinner from "@rui/LoadSpinner";
import Stack from "@rui/Stack";

import BillingCreditCheckoutBreakdown from "./BillingCreditCheckoutBreakdown";
import BillingCreditCheckoutDetails from "./BillingCreditCheckoutDetails";
import BillingCreditCheckoutPurchase from "./BillingCreditCheckoutPurchase";
import { CREDIT_PACKAGES } from "../constants";
import { LeftPanel, RightPanel } from "../styles";

const BillingCreditCheckout = () => {
  const [step, setStep] = useState<BillingCreditCheckoutStep>("credits");
  usePageTitle(`Billing - Credits Checkout - ${titleize(step)}`);

  const { checkoutBillingInfo, initiateUploadCreditCheckout } = useCheckout();

  const [selectedPackage, setSelectedPackage] = useState(CREDIT_PACKAGES[0]);

  const [paymentInfoElementOptions, setPaymentInfoElementOptions] =
    useState<PaymentFormElementOptions | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<PreviewInvoice | null>(
    null
  );

  const performInitiateUploadCreditCheckout = (
    billingInfo?: CheckoutBillingInfo,
    options?: {
      onSuccess?: (response: InitiateCheckoutResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    initiateUploadCreditCheckout.mutate(
      {
        billingInfo,
        quantity: selectedPackage.quantity,
      },
      {
        onSuccess: (response) => {
          setPreviewInvoice(response.invoice);
          setPaymentInfoElementOptions({
            clientSecret: response.clientSecret,
            type: response.clientSecretType,
          });
          options?.onSuccess?.(response);
        },
        onError: (error) => {
          options?.onError?.(error);
          if (billingInfo?.address) {
            showToast({
              severity: "error",
              message:
                "Oops! Something went wrong and your address could not be saved. Please confirm your address or reach out to support@read.ai if the problem persists.",
            });
          } else {
            showToast({
              severity: "error",
              message:
                "Oops! Something went wrong and checkout cannot be completed at this time.",
              sticky: true,
            });
          }
        },
      }
    );
  };

  const handleChangeBillingInfo = (
    billingInfo: CheckoutBillingInfo,
    options?: {
      onSuccess?: (response: InitiateCheckoutResponse) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    performInitiateUploadCreditCheckout(billingInfo, options);
  };

  const handleContinueToPurchase = () => {
    setStep("purchase");
    const existingAddress =
      BillingUtils.getFormattedAddress(checkoutBillingInfo);
    if (existingAddress) {
      performInitiateUploadCreditCheckout(checkoutBillingInfo!);
    }
  };

  const handleChangePackage = (newPackage: CreditPackage) =>
    setSelectedPackage(newPackage);

  return (
    <Stack direction="row" sx={{ height: "100%" }}>
      <LeftPanel variant="center">
        <BillingCreditCheckoutBreakdown
          isLoading={initiateUploadCreditCheckout.isPending}
          invoice={previewInvoice}
          selectedPackage={selectedPackage}
        />
      </LeftPanel>
      <RightPanel variant="center">
        <CheckoutStepper currentPage={step} type="credits" />
        {step === "credits" ? (
          <BillingCreditCheckoutDetails
            isLoading={initiateUploadCreditCheckout.isPending}
            onChange={handleChangePackage}
            onContinue={handleContinueToPurchase}
            selectedPackage={selectedPackage}
          />
        ) : step === "purchase" ? (
          <BillingCreditCheckoutPurchase
            isLoading={initiateUploadCreditCheckout.isPending}
            onChangeBillingInfo={handleChangeBillingInfo}
            paymentInfoElementOptions={paymentInfoElementOptions}
            selectedPackage={selectedPackage}
          />
        ) : (
          <LoadSpinner />
        )}
      </RightPanel>
    </Stack>
  );
};

export default BillingCreditCheckout;
